import { axiosSecuredInstance } from "configurations/axiosConfig";
import { IColumn } from "entities/ApiModel/IColumn";

export const getColumnSettings = async (
  gridName: string
): Promise<IColumn[]> => {
  try {
    const { data } = await axiosSecuredInstance.get(
      `/UISettings/grids/columnSettings/${gridName}`
    );
    return Array.isArray(data) ? data : [];
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    console.error(
      `getColumnSettings(${gridName}) failed (status=${status ?? "n/a"})`,
      { error, response: data }
    );
    return [];
  }
};

export const saveColumnSettings = async (
  gridName: string,
  columns: IColumn[]
): Promise<IColumn[]> => {
  try {
    const { data } = await axiosSecuredInstance.post(
      `/UISettings/grids/columnSettings/${gridName}`,
      columns
    );
    return Array.isArray(data) ? data : columns;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    console.error(
      `saveColumnSettings(${gridName}) failed (status=${status ?? "n/a"})`,
      { error, response: data }
    );
    return columns;
  }
};

// Shared "Rows per page" footer setting, persisted under the
// `grids.rowsPerPage` user-preference key. Single value across all grids.
const DEFAULT_ROWS_PER_PAGE = 10;
let cachedRowsPerPagePromise: Promise<number> | null = null;
// Sync mirror of the resolved value. Lets the hook seed its useState with the
// correct value on tab activation (after the first GET in the session)
// instead of flashing through the fallback.
let cachedRowsPerPageValue: number | null = null;

export const getCachedRowsPerPageSync = (): number | null =>
  cachedRowsPerPageValue;

export const getRowsPerPageSetting = (): Promise<number> => {
  if (cachedRowsPerPagePromise) return cachedRowsPerPagePromise;
  cachedRowsPerPagePromise = axiosSecuredInstance
    .get<number>("/UISettings/grids/rowsPerPageSetting")
    .then(({ data }) => {
      const value =
        typeof data === "number" && data > 0 ? data : DEFAULT_ROWS_PER_PAGE;
      cachedRowsPerPageValue = value;
      return value;
    })
    .catch((error: any) => {
      const status = error?.response?.status;
      console.error(
        `getRowsPerPageSetting failed (status=${status ?? "n/a"})`,
        error
      );
      cachedRowsPerPagePromise = null;
      return DEFAULT_ROWS_PER_PAGE;
    });
  return cachedRowsPerPagePromise;
};

export const setRowsPerPageSetting = async (
  count: number
): Promise<number> => {
  try {
    const { data } = await axiosSecuredInstance.post<number>(
      "/UISettings/grids/rowsPerPageSetting",
      null,
      { params: { count } }
    );
    const next = typeof data === "number" ? data : count;
    // Refresh both caches so subsequent reads see the new value without a refetch.
    cachedRowsPerPagePromise = Promise.resolve(next);
    cachedRowsPerPageValue = next;
    return next;
  } catch (error: any) {
    const status = error?.response?.status;
    console.error(
      `setRowsPerPageSetting(${count}) failed (status=${status ?? "n/a"})`,
      error
    );
    return count;
  }
};
